import { MeSchema, PolicySchema } from '@ametista-ai/api-schema';
import { ApiResponse } from '@ametista-ai/api-schema/http/types';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { agenciesKeys } from '@/queries/agencies/keys';
import { apiClient } from '@/stores/api/apiClient';
import { meKeys } from './keys';

export type MeResult = MeSchema & { policies: PolicySchema[] };

/**
 * Fetches the current session and warms the agency cache from the same
 * response. Exported standalone (not just inside useMe's queryFn) so route
 * guards can await it directly via `queryClient.fetchQuery` and branch on
 * the result, the same way they did with the old AuthStore.fetch().
 */
export async function fetchMe(queryClient: QueryClient): Promise<MeResult> {
  const response = await apiClient.get<ApiResponse<MeSchema>>('/v1/me', {});

  if (response.data.agency) {
    queryClient.setQueryData(agenciesKeys.item(response.data.agency.id), response.data.agency);
  }

  return { ...response.data, policies: response.policies ?? [] };
}

export function useMe(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: meKeys.all,
    queryFn: () => fetchMe(queryClient),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Flattens the current agency's abilities to booleans (an array value means
 * membership-based access — non-empty is granted). Prefer the
 * `useAgencyAbilities()` hook in components; this is exported mainly so that
 * hook has somewhere to put the flattening logic.
 */
export function getAgencyAbilities(me: MeResult | undefined): Record<string, boolean> {
  const policy = me?.policies.find((p) => p.id === me.agency?.id);
  const flattened: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(policy?.abilities ?? {})) {
    flattened[key] = Array.isArray(value) ? value.length > 0 : value;
  }
  return flattened;
}
